import type { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { resolveHttpError } from '@indutrust/shared-util';
import { mapResponse } from '@ngrx/operators';
import { signalStore, withState } from '@ngrx/signals';
import {
  Events,
  on,
  withEventHandlers,
  withReducer,
} from '@ngrx/signals/events';
import { switchMap } from 'rxjs';
import { FactoryActionsApiClient } from './factory-actions-api-client.service';
import { factoryApprovalEvents } from './factory-approval.events';

type Phase = 'idle' | 'submitting' | 'submitted' | 'publishing' | 'published';

/**
 * Workflow store driving the factory approval pipeline.
 *
 * Exposes two logically-separate per-phase statuses because submit and
 * publish can fail independently, but are part of the same lifecycle; the
 * `phase` signal advances `idle → submitting → submitted → publishing →
 * published`.
 *
 * @example
 * const dispatch = injectDispatch(factoryApprovalEvents);
 * dispatch.submitForApprovalRequested({ factoryId: 7 });
 */
export const FactoryApprovalStore = signalStore(
  { providedIn: 'root' },
  withState<{
    phase: Phase;
    submitError: string | null;
    publishError: string | null;
  }>({
    phase: 'idle',
    submitError: null,
    publishError: null,
  }),
  withReducer(
    on(factoryApprovalEvents.submitForApprovalRequested, () => ({
      phase: 'submitting',
      submitError: null,
    })),
    on(factoryApprovalEvents.submitForApprovalSucceeded, () => ({
      phase: 'submitted',
    })),
    on(factoryApprovalEvents.submitForApprovalFailed, ({ payload }) => ({
      phase: 'idle',
      submitError: payload.error,
    })),
    on(factoryApprovalEvents.publishRequested, () => ({
      phase: 'publishing',
      publishError: null,
    })),
    on(factoryApprovalEvents.publishSucceeded, () => ({
      phase: 'published',
    })),
    on(factoryApprovalEvents.publishFailed, ({ payload }) => ({
      phase: 'submitted',
      publishError: payload.error,
    }))
  ),
  withEventHandlers(
    (
      _,
      events = inject(Events),
      actions = inject(FactoryActionsApiClient)
    ) => ({
      submit$: events.on(factoryApprovalEvents.submitForApprovalRequested).pipe(
        switchMap(({ payload }) =>
          actions.submitForApproval(payload.factoryId).pipe(
            mapResponse({
              next: (factory) =>
                factoryApprovalEvents.submitForApprovalSucceeded({ factory }),
              error: (error: HttpErrorResponse) =>
                factoryApprovalEvents.submitForApprovalFailed({
                  error: resolveHttpError(error).code,
                }),
            })
          )
        )
      ),

      publish$: events.on(factoryApprovalEvents.publishRequested).pipe(
        switchMap(({ payload }) =>
          actions.publish(payload.factoryId).pipe(
            mapResponse({
              next: (factory) =>
                factoryApprovalEvents.publishSucceeded({ factory }),
              error: (error: HttpErrorResponse) =>
                factoryApprovalEvents.publishFailed({
                  error: resolveHttpError(error).code,
                }),
            })
          )
        )
      ),
    })
  )
);
